import { useEffect, useRef, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { registerDeviceToken, showToast, incrementUnreadCount } from '../store/slices/notificationSlice';
import { CATEGORY_DEEP_LINK_MAP } from '../constants/notifications';

const useFCM = () => {
  const dispatch = useDispatch();
  const tokenRef = useRef(null);
  const initializedRef = useRef(false);
  const [permissionState, setPermissionState] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'default'
  );

  // Check if incoming call modal is open to suppress duplicate consultation toasts
  const incomingCallModalOpen = useSelector(
    (state) => state.consultation?.incomingCallModalOpen || false
  );
  const incomingCallRef = useRef(incomingCallModalOpen);
  useEffect(() => {
    incomingCallRef.current = incomingCallModalOpen;
  }, [incomingCallModalOpen]);

  const activate = useCallback(async () => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    // Guard: Notification API must exist (insecure contexts lack it)
    if (typeof Notification === 'undefined' || !('serviceWorker' in navigator)) {
      return;
    }

    // Lazy-import fcmService to avoid loading Firebase for anonymous users
    const { initFCM, requestPermissionAndGetToken, onForegroundMessage } = await import('../services/fcmService');

    const messaging = await initFCM();
    if (!messaging) return; // Firebase not configured — silent no-op

    const currentPermission = Notification.permission;
    setPermissionState(currentPermission);

    let token = null;

    if (currentPermission === 'granted') {
      // Already granted — skip prompt, go straight to token
      token = await requestPermissionAndGetToken();
    } else if (currentPermission === 'default') {
      // First time — triggers browser native prompt
      token = await requestPermissionAndGetToken();
      setPermissionState(Notification.permission);
    }
    // 'denied' — do nothing, no nag

    if (token) {
      tokenRef.current = token;
      localStorage.setItem('fcm_device_token', token);
      try {
        await dispatch(registerDeviceToken({ token, platform: 'web' })).unwrap();
      } catch (err) {
        console.warn('[FCM] Token registration API failed:', err);
      }
    }

    // Register foreground message handler
    onForegroundMessage((payload) => {
      const data = payload.data || {};
      const notification = payload.notification || {};
      const category = data.category || '';
      const title = notification.title || data.title || 'Notification';
      const body = notification.body || data.body || '';
      const deepLink = data.deep_link || CATEGORY_DEEP_LINK_MAP[category] || '/notifications';

      // Consultation messages: suppress only when incoming call modal is visible
      if (category === 'consultation' && incomingCallRef.current) {
        return; // Live connection is handling it
      }

      dispatch(showToast({ title, body, category, deepLink }));
      dispatch(incrementUnreadCount());
    });
  }, [dispatch]);

  const deactivate = useCallback(() => {
    // Token cleanup is handled in performLogout via localStorage
    tokenRef.current = null;
    initializedRef.current = false;
  }, []);

  return { activate, deactivate, permissionState };
};

export default useFCM;
